import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {fetchTranslations} from "../types/translationsClients";
import DocklineFX from "../components/DocklineFX";
import HeroFX from "../components/HeroFX";
import {Helmet} from 'react-helmet-async';

const Development = () => {
    const navigate = useNavigate();

    const [dict, setDict] = useState<Record<string, string> | null>(null);
    const [logos, setLogos] = useState<string[]>([]);
    const locale = "de";
    const namespace = "landing";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchTranslations({
                    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
                    locale,
                    namespace
                });
                if (!cancelled) setDict(data.items);
            } catch (e) {
                if (!cancelled) setDict({});
                console.error("Failed to load translations (landing)", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const bgStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none"
    };

    // Landing hero parallax + tilt on scroll with visibility toggle
    useEffect(() => {
        const hero = document.querySelector('.landing-hero-cover') as HTMLElement | null;
        if (!hero) return;

        const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

        let rafId = 0;
        const loop = () => {
            const doc = document.documentElement;
            const st = doc.scrollTop || document.body.scrollTop || 0;

            // Calculate halfway point of the total scrollable distance
            const maxScroll = Math.max(1, doc.scrollHeight - doc.clientHeight);
            const hideThreshold = 0.5 * maxScroll; // hide beyond 50% of page height

            if (st >= hideThreshold) {
                // Hide hero when scrolled beyond threshold
                hero.classList.add('lh-hidden');
            } else {
                // Show hero and continue parallax/tilt while above threshold
                hero.classList.remove('lh-hidden');

                const vh = window.innerHeight || 1;
                // Map the first ~140% of viewport height to 0..1 progress (slower progression)
                const t = clamp(st / (vh * 1.4));

                // Smaller effect so it lasts longer: scale 1.0 → 0.90, rotateX 0deg → 10deg
                const scale = 1 - 0.20 * t;
                const tilt = 10 * t;

                // Keep anchored while adding perspective, scaling, and tilt
                hero.style.transform = `translateX(-50%) perspective(1000px) scale(${scale}) rotateX(${tilt}deg)`;
            }

            rafId = requestAnimationFrame(loop);
        };

        rafId = requestAnimationFrame(loop);
        return () => {
            hero.classList.remove('lh-hidden');
            cancelAnimationFrame(rafId);
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>Webentwicklung & Softwareentwicklung in München | Dockline Development</title>
                <meta
                    name="description"
                    content="Dockline Development entwickelt moderne Web- und Softwarelösungen für Unternehmen in München und Deutschland – skalierbar, performant und professionell umgesetzt."
                />
                <meta
                    name="keywords"
                    content="Webentwicklung München, Softwareentwicklung München, Full Stack Developer München, Web Developer München, App Entwicklung München, Dockline Development"
                />
                <link rel="canonical" href="https://dockline.digital/dev"/>

                {/* Open Graph */}
                <meta property="og:type" content="website"/>
                <meta property="og:title" content="Dockline Development – Web & Software Engineering in München"/>
                <meta
                    property="og:description"
                    content="Moderne Webentwicklung, Full‑Stack Engineering und App‑Entwicklung in München. Dockline Development – digitale Lösungen, die skalieren."
                />
                <meta property="og:url" content="https://dockline.digital/dev"/>
                <meta property="og:image" content="https://dockline.digital/pics/og/devs-og.jpg"/>
                <meta property="og:locale" content="de_DE"/>

                {/* JSON-LD schema for SoftwareDevelopmentService */}
                <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Dockline Development",
              "url": "https://dockline.digital/dev",
              "logo": "https://dockline.digital/pics/logo/sec/ddevs_logo_sec.svg",
              "serviceType": "Webentwicklung & Softwareentwicklung",
              "areaServed": {
                "@type": "City",
                "name": "München",
                "addressCountry": "DE"
              },
              "description": "Professionelle Webentwicklung, Full‑Stack Engineering und Softwareentwicklung in München.",
              "provider": {
                "@type": "Organization",
                "name": "Dockline Digital"
              }
            }
            `}</script>
                {/* JSON-LD FAQ schema */}
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "What kind of projects does Dockline Development build?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Dockline Development builds marketing websites, web applications, internal tools, dashboards and custom software that connect to your existing systems. We focus on clean, maintainable solutions that solve specific business problems."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Do you only work with clients in Munich?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "We are based in Munich, but software projects typically run fully remote. We work with clients all across Germany, using video meetings and online collaboration so you do not need to be on site for a project to work well."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "How is web and software development priced?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Web and software development are usually billed based on the actual hours required. During the first briefing call we discuss your goals and constraints, then provide a realistic hourly estimate so you know what range to expect before we start."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Can you work with existing systems or legacy code?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Yes. Many projects involve extending existing systems, integrating third-party tools or gradually improving legacy code. We start by understanding the current setup, then propose realistic steps to stabilise, refactor or migrate where it adds value."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Do you also handle hosting, deployment and maintenance?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "We can take care of deployment, basic monitoring and updates, or work with your existing infrastructure team. Depending on your needs, we help choose suitable hosting, set up CI/CD pipelines and define a maintenance model that keeps your application healthy over time."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "What technologies do you use?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Dockline Development typically works with modern web technologies such as React, TypeScript, Node.js, PHP, relational databases and containerised infrastructure. The stack is chosen based on what best fits your use case, team and long-term plans."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "How does a typical project start?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "A typical project starts with a short call to clarify goals, constraints and success criteria. From there we outline a proposal with milestones, scope and an estimate. Once agreed, we move into discovery and design before implementation."
                      }
                    }
                  ]
                }
                `}</script>
            </Helmet>

            <div style={{'--fx-color': '#5C33F6', '--fx-glow': 'rgba(92,51,246,0.35)'} as React.CSSProperties}>
                <div className="landing-hero-cover hero-anim">
                    <img src="/pics/landingPref3Dev.webp" alt="" className="landing-hero" id="landing-hero"/>
                    <HeroFX/>
                    <div className="landing-hero-centered-text">
                        <h1 className="dev-color">Dockline Devs</h1>
                        <h2>Full Stack Software Engineering</h2>
                    </div>
                    <a
                        className="landing-hero-bottom-hint"
                        onClick={() => window.scrollTo({top: window.innerHeight, behavior: 'smooth'})}
                        style={{cursor: 'pointer'}}
                    >
                        Scroll to Explore
                    </a>
                </div>

                <div className="content">
                    <DocklineFX color="#5C33F6"/>

                    {/* === Devs Intro (like Studios/Systems) === */}
                    <section className="devs-intro">
                        <div className="section-intro">
                            <h2 className="dev-color">What Dockline Devs Does</h2>
                            <p>
                                Dockline Development builds modern websites, web applications, and custom software solutions tailored to your workflow. We combine clean design, performance, and scalability to create tools that support real business growth.
                            </p>
                        </div>
                    </section>

                    {/* === Vibe to Production Banner === */}
                    <section className="vibe-banner">
                        <div className="vibe-banner__eyebrow">New</div>
                        <h2>Vibecoded it? <span>Now let's build it right.</span></h2>
                        <p>
                            You proved the concept with AI-generated code — great start.
                            Now hire a real developer to turn it into production-grade software:
                            clean architecture, proper security, and code that scales and survives.
                        </p>
                        <Link to="/" state={{scrollTo: 'contact-section'}}>
                            <button><p>Let's talk about your prototype</p></button>
                        </Link>
                    </section>

                    <div className="munich-local-link">
                        <Link to="/webentwickler-muenchen" className="local-link-button">
                            Webentwickler in München →
                        </Link>
                    </div>

                    {/* === Our Work (grid) === */}
                    <section className="devs-work" aria-labelledby="devs-work-title">
                        <div className="section-intro">
                            <h2 id="devs-work-title">What we build</h2>
                            <p>Fast websites, tailored software, and a reliable reporting pipeline.</p>
                        </div>
                        <div className="devs-work__cards">
                            <article className="devs-card">
                                <i className="bi bi-window"></i>
                                <h3>Website Design &amp; CMS</h3>
                                <p>
                                    Custom‑designed, fast websites with a built‑in client admin to update texts and
                                    photos anytime.
                                </p>
                            </article>

                            <article className="devs-card">
                                <i className="bi bi-braces"></i>
                                <h3>Web Applications (Full‑Stack)</h3>
                                <p>
                                    Business web apps built end‑to‑end — dashboards, internal tools, integrations,
                                    authentication, roles, exports, and more.
                                </p>
                            </article>

                            <article className="devs-card">
                                <i className="bi bi-apple"></i>
                                <h3>Apple Platform Apps</h3>
                                <p>
                                    Native apps for iOS, iPadOS, and macOS (Swift/SwiftUI) — from rapid prototypes to
                                    production, App&nbsp;Store or enterprise.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* === How We Build (reuse process styles) === */}
                    <section className="process-snapshot" aria-labelledby="devs-process-title">
                        <div className="section-intro">
                            <h2 id="devs-process-title">How we build</h2>
                            <p>Lean discovery, pragmatic design, disciplined implementation, and dependable
                                operations.</p>
                        </div>
                        <ol className="process-steps">
                            <li className="process-step">
                                <div className="step-badge">1</div>
                                <i className="bi bi-search"></i>
                                <h3>Discover</h3>
                                <p>Understand goals, users, and constraints. Translate them into clear, testable
                                    requirements.</p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">2</div>
                                <i className="bi bi-pencil-square"></i>
                                <h3>Design</h3>
                                <p>UX flows, data models, and architecture — designed for clarity and scale.</p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">3</div>
                                <i className="bi bi-gear"></i>
                                <h3>Build</h3>
                                <p>Agile sprints, code review, and CI/CD. Maintainable code, fast feedback, fewer
                                    surprises.</p>
                            </li>
                            <li className="process-step">
                                <div className="step-badge">4</div>
                                <i className="bi bi-bar-chart-line"></i>
                                <h3>Run</h3>
                                <p>Deploy, monitor, and improve. Telemetry and quality gates keep things stable.</p>
                            </li>
                        </ol>
                    </section>

                    {/* === Reference Projects (thumbnails you can replace) === */}
                    <section className="devs-references" aria-labelledby="devs-references-title">
                        <div className="section-intro">
                            <h2 id="devs-references-title">Reference Projects</h2>
                            <p>Some of our past projects.</p>
                        </div>
                        <div className="devs-ref__grid">
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/clientadmin.webp" alt="Website reference 1"/>
                                <figcaption>Website Client Admin — Dockline Devs Product</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/crm.webp" alt="Website reference 2"/>
                                <figcaption>CRM — Custom Web Applicatioon</figcaption>
                            </figure>
                            <figure
                                className="media-frame devs-ref__item"
                                onClick={() => window.open('https://maike-freese-spott.de/', '_blank', 'noopener,noreferrer')}
                                style={{cursor: 'pointer'}}
                            >
                                <img src="/pics/devs/maike.webp" alt="Website reference 2"/>
                                <figcaption>Website — Development</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/ddweb.webp" alt="Client admin page screenshot"/>
                                <figcaption>Website — Design and Development</figcaption>
                            </figure>
                            <figure
                                className="media-frame devs-ref__item"
                                onClick={() => window.open('https://jack-russell-consulting.de/', '_blank', 'noopener,noreferrer')}
                                style={{cursor: 'pointer'}}
                            >
                                <img src="/pics/devs/jrcweb.webp" alt="Reporting tool example 1"/>
                                <figcaption>Website — Design and Development</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/sarepo.webp" alt="Reporting tool example 2"/>
                                <figcaption>Reporting Tool — Custom web app</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/russellgpt.webp" alt="Reporting tool example 2"/>
                                <figcaption>Private GPT — Custom web app</figcaption>
                            </figure>
                            <figure className="media-frame devs-ref__item">
                                <img src="/pics/devs/pdfexport.webp" alt="Reporting tool example 1"/>
                                <figcaption>PDF Report Tool — Custom Python Application</figcaption>
                            </figure>
                            <figure
                                className="media-frame devs-ref__item"
                                onClick={() => window.open('https://das-tragwerk.com/', '_blank', 'noopener,noreferrer')}
                                style={{cursor: 'pointer'}}
                            >
                                <img src="/pics/devs/tragwerk.webp" alt="Reporting tool example 1"/>
                                <figcaption>Website — Design and Development</figcaption>
                            </figure>
                            <figure
                                className="media-frame devs-ref__item"
                                onClick={() => window.open('https://superangels.fund/', '_blank', 'noopener,noreferrer')}
                                style={{cursor: 'pointer'}}
                            >
                                <img src="/pics/devs/superangels.webp" alt="Reporting tool example 1"/>
                                <figcaption>Website — Design and Development</figcaption>
                            </figure>
                        </div>
                    </section>

                    {/* === Tech Stack Ticker === */}
                    <section className="devs-stack" aria-labelledby="devs-stack-title">
                        <div className="section-intro">
                            <h2 id="devs-stack-title">Our Stack</h2>
                            <p>Modern, well-supported technologies that balance velocity and stability.</p>
                        </div>
                        <div className="stack-ticker" aria-hidden="true">
                            <div className="stack-ticker__track">
                                <img src="/pics/icons/react.svg" alt="React"/>
                                <img src="/pics/icons/typescript.svg" alt="TypeScript"/>
                                <img src="/pics/icons/nodejs.svg" alt="Node.js"/>
                                <img src="/pics/icons/php.svg" alt="PHP"/>
                                <img src="/pics/icons/mysql.svg" alt="MySQL"/>
                                <img src="/pics/icons/docker.svg" alt="Docker"/>
                                <img src="/pics/icons/nginx.svg" alt="Nginx"/>
                                <img src="/pics/icons/aws.svg" alt="AWS"/>
                                <img src="/pics/icons/azure.svg" alt="Azure"/>
                                <img src="/pics/icons/git.svg" alt="Git"/>
                            </div>
                        </div>
                    </section>

                    {/* === Why Dockline Devs === */}
                    <section className="systems-why" aria-labelledby="devs-why-title">
                        <div className="section-intro">
                            <h2 id="devs-why-title" className="dev-color">Why Dockline Devs?</h2>
                            <p>
                                Dockline Devs offers modern web and software development with a lean, highly personal setup.
                                You work directly with the engineer building your product — fast decisions, transparent communication
                                and solutions tailored to your exact workflow.
                            </p>
                        </div>

                        <div className="systems-why__grid">
                            <article className="systems-card systems-why__item">
                                <h3>Direct access to your developer</h3>
                                <p>
                                    No account managers, no hand-offs, no delays. You communicate directly with the person
                                    designing, coding and maintaining your product. This keeps feedback loops short and
                                    ensures your requirements are understood immediately.
                                </p>
                            </article>

                            <article className="systems-card systems-why__item">
                                <h3>Modern, scalable tech stack</h3>
                                <p>
                                    As an active software engineer and computer science student, I work with up-to-date tools and
                                    modern industry practices — React, TypeScript, Node.js, Swift, CI/CD and structured engineering
                                    workflows. Your product is built for performance, clarity and long-term maintainability.
                                </p>
                            </article>

                            <article className="systems-card systems-why__item">
                                <h3>Lean structure, fair project rates</h3>
                                <p>
                                    Dockline Devs operates without large-agency overhead, making it an attractive option for
                                    small and medium-sized businesses. You receive professional engineering with
                                    <em> fair, transparent hourly rates</em> and reliable estimates given at the start of each project.
                                </p>
                            </article>
                        </div>
                    </section>

                    {/* === FAQ === */}
                    <section className="systems-faq" aria-labelledby="devs-faq-title">
                        <div className="section-intro">
                            <h2 id="devs-faq-title" className="dev-color">Frequently Asked Questions</h2>
                            <p>
                                A few common questions about how Dockline Development approaches web and software
                                projects. If you do not see your situation here, just reach out and we are happy to
                                discuss it.
                            </p>
                        </div>
                        <div className="faq-list">
                            <details className="faq-item">
                                <summary><p className="boldText">What kind of projects does Dockline Development build?</p></summary>
                                <p>
                                    Dockline Development builds marketing websites, web applications, internal tools,
                                    dashboards and custom software that connect to your existing systems. We focus on
                                    clean, maintainable solutions that solve specific business problems instead of just
                                    adding more features.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Do you only work with clients in Munich?</p></summary>
                                <p>
                                    We are based in Munich, but most software projects run fully remote. We work with
                                    clients all across Germany and typically collaborate via video calls and online
                                    tools, so you do not have to be local for a project to work smoothly.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">How is web and software development priced?</p></summary>
                                <p>
                                    Web and software development are usually billed per hour. In a first briefing call
                                    we clarify your goals and constraints and then provide a realistic hourly estimate
                                    so you have a clear idea of the expected range before we start.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Can you work with existing systems or legacy code?</p></summary>
                                <p>
                                    Yes. Many engagements involve extending existing systems, integrating third-party
                                    services or gradually improving legacy code. We start by understanding the current
                                    architecture and then suggest realistic steps to stabilise, clean up or migrate
                                    where it adds the most value.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">Do you also handle hosting, deployment and maintenance?</p></summary>
                                <p>
                                    We can support you with deployment, basic monitoring and updates, or work alongside
                                    your existing infrastructure team. Depending on your needs, we help choose suitable
                                    hosting, set up CI/CD pipelines and define a maintenance approach that keeps your
                                    application running smoothly.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">What technologies do you use?</p></summary>
                                <p>
                                    We typically work with modern web technologies such as React, TypeScript, Node.js,
                                    PHP, relational databases and containerised infrastructure. The exact stack depends
                                    on what fits your use case, your team and the long-term direction of the product.
                                </p>
                            </details>
                            <details className="faq-item">
                                <summary><p className="boldText">How does a typical project start?</p></summary>
                                <p>
                                    A typical project starts with a short conversation about goals, constraints and
                                    success criteria. From there we outline a proposal with milestones, scope and an
                                    estimate. Once agreed, we move into discovery and design before implementation.
                                </p>
                            </details>
                        </div>
                    </section>
                    {/* === CTA === */}
                    <section className="systems-cta">
                        <h2 className="dev-color">Let’s build your next idea</h2>
                        <p id="systems-cta-p">Websites, apps, or custom tools — tell us what you need and we’ll help you
                            bring it to life.</p>
                        <Link to="/" state={{scrollTo: 'contact-section'}}>
                            <button><p>Get in Touch</p></button>
                        </Link>
                    </section>
                </div>
            </div>
        </>
    );
};
export default Development;